/**
 * <bigens-sticky-bar> (T41b → T45): the sticky set bar on the PDP (mockup D), desktop and phone. Rendered inside the deal builder
 * block, which owns the state and calls `bar.update(...)` on every change; this element only decides when to show.
 *
 * - Shows once the main add-to-cart button has scrolled out above the viewport (IntersectionObserver), hides while
 *   that button is on screen, not yet reached, or when the footer is in view (same rules as Horizon's mobile bar).
 * - Desktop (≥ 750px): shows once the main button has scrolled out above the viewport.
 * - Phones (T45, AUD2 #4): shows from load, whenever the main button is off screen (not yet reached or scrolled past),
 *   with the compact set summary ("2 balls · €161,72" / "save 10% · add 1 more for 15%" + Add). Horizon's own mobile
 *   bar is hidden on builder PDPs (bigens-sticky-bar.css). The media query is watched live.
 * - Disabled (never shown) in the waitlist state or while the main button is disabled: `bar.update({ enabled })`.
 * - Its button clicks the main add-to-cart button as a puppet, so the same product form (and the builder's
 *   getCartItems) runs. No copy in JS: the labels are passed in by the owner.
 *
 * bar.target = HTMLButtonElement   the main add-to-cart button to watch and click
 * bar.update({ enabled, title, summary, label, total, thumb })
 */
class BigensStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #buttonObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {HTMLButtonElement | null} */
  #target = null;

  #enabled = false;
  #buttonAbove = false;
  #buttonVisible = false;
  #footerVisible = false;
  #media = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#media.addEventListener('change', this.#evaluate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#media.removeEventListener('change', this.#evaluate);
    this.#disconnectObservers();
  }

  /** @param {HTMLButtonElement | null} button */
  set target(button) {
    if (button === this.#target) return;
    this.#disconnectObservers();
    this.#target = button;
    this.#buttonAbove = false;
    this.#buttonVisible = false;
    if (!button) {
      this.#evaluate();
      return;
    }

    this.#buttonObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        this.#buttonAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.#buttonVisible = entry.isIntersecting;
        this.#evaluate();
      },
      { threshold: 0 },
    );
    this.#buttonObserver.observe(button);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;
          this.#footerVisible = entry.isIntersecting;
          this.#evaluate();
        },
        { rootMargin: '200px 0px 0px 0px' },
      );
      this.#footerObserver.observe(footer);
    }
  }

  get target() {
    return this.#target;
  }

  /**
   * @param {{ enabled?: boolean, title?: string, summary?: string, label?: string, total?: string, thumb?: string, phoneTitle?: string, phoneSub?: string, phoneLabel?: string }} state
   */
  update({ enabled, title, summary, label, total, thumb, phoneTitle, phoneSub, phoneLabel }) {
    if (typeof enabled === 'boolean') this.#enabled = enabled;
    this.#setText('[data-sticky-title]', title);
    this.#setText('[data-sticky-summary]', summary);
    this.#setText('[data-sticky-label]', label);
    this.#setText('[data-sticky-total]', total);
    this.#setText('[data-sticky-phone-title]', phoneTitle);
    this.#setText('[data-sticky-phone-sub]', phoneSub);
    this.#setText('[data-sticky-phone-label]', phoneLabel);

    const image = this.querySelector('[data-sticky-thumb]');
    if (image instanceof HTMLImageElement) {
      if (thumb && image.getAttribute('src') !== thumb) image.src = thumb;
      image.hidden = !thumb && !image.getAttribute('src');
    }

    this.#evaluate();
  }

  /**
   * @param {string} selector
   * @param {string | undefined} value
   */
  #setText(selector, value) {
    if (value === undefined) return;
    const node = this.querySelector(selector);
    if (!node) return;
    node.textContent = value;
    if (node instanceof HTMLElement) node.hidden = value === '';
  }

  #evaluate = () => {
    const placed = this.#media.matches ? this.#buttonAbove : !this.#buttonVisible;
    const show = this.#enabled && placed && !this.#footerVisible && !!this.#target;
    this.dataset.stuck = String(show);
    this.setAttribute('aria-hidden', String(!show));
    const button = this.querySelector('[data-sticky-atc]');
    if (button instanceof HTMLButtonElement) button.tabIndex = show ? 0 : -1;
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-sticky-atc]')) return;
    if (!this.#target || this.#target.disabled) return;
    this.#target.dataset.puppet = 'true';
    this.#target.click();
    this.dataset.added = 'true';
    window.setTimeout(() => this.removeAttribute('data-added'), 1200);
  };

  #disconnectObservers() {
    this.#buttonObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#buttonObserver = null;
    this.#footerObserver = null;
    this.#footerVisible = false;
  }
}

if (!customElements.get('bigens-sticky-bar')) {
  customElements.define('bigens-sticky-bar', BigensStickyBar);
}
